import { ReactElement } from 'react';

import {
  ChakraFlex,
  ChakraGridItem,
  ChakraText,
  EquipUITheme,
  RiskLevel,
  getAgeFromDob,
} from '@equip.health/ui';
import { inRange } from 'lodash';

import { SurveyBadge } from '~/components/maud/patients/profile/common/SurveyBadge';
import { SurveyRiskBadge } from '~/components/maud/patients/profile/common/SurveyRiskBadge';
import { PatientGender } from '~/lib/constants/patients.constants';

import { sumSpecificKeysLegacy } from './survey.util';
import util from './util';

const { paragraphBold } = EquipUITheme.typography;

function isLifetimeTrue(question: [{ Lifetime?: boolean }]) {
  return question?.[0]?.Lifetime;
}

function isPast1MonthTrue(question: [{ ['Past 1 Month']?: boolean }]) {
  return question?.[0]?.['Past 1 Month'];
}

const CssrsRiskLevelToLabelMap: Partial<Record<RiskLevel, string>> = {
  [RiskLevel.LOW]: 'Low risk',
  [RiskLevel.MEDIUM]: 'Medium risk',
  [RiskLevel.HIGH]: 'High risk',
};

export function calculateCssrsLifetimeScoreAndRiskLabel(
  jsonResults: Record<string, any>,
): {
  lifetime: { score: number; risk: RiskLevel };
  pastMonth: { score: number; risk: RiskLevel };
} {
  const result = {
    lifetime: { score: 0, risk: RiskLevel.LOW },
    pastMonth: { score: 0, risk: RiskLevel.LOW },
  };

  const questions = [
    jsonResults['c-ssrs-lr-1'],
    jsonResults['c-ssrs-lr-2'],
    jsonResults['c-ssrs-lr-3'],
    jsonResults['c-ssrs-lr-4'],
    jsonResults['c-ssrs-lr-5'],
  ];
  if (isLifetimeTrue(questions[4])) {
    result.lifetime.score = 5;
    result.lifetime.risk = RiskLevel.HIGH;
  } else if (isLifetimeTrue(questions[3])) {
    result.lifetime.score = 4;
    result.lifetime.risk = RiskLevel.HIGH;
  } else if (isLifetimeTrue(questions[2])) {
    result.lifetime.score = 3;
    result.lifetime.risk = RiskLevel.HIGH;
  } else if (isLifetimeTrue(questions[1])) {
    result.lifetime.score = 2;
    result.lifetime.risk = RiskLevel.MEDIUM;
  } else if (isLifetimeTrue(questions[0])) {
    result.lifetime.score = 1;
    result.lifetime.risk = RiskLevel.LOW;
  }

  if (isPast1MonthTrue(questions[4])) {
    result.pastMonth.score = 5;
    result.pastMonth.risk = RiskLevel.HIGH;
  } else if (isPast1MonthTrue(questions[3])) {
    result.pastMonth.score = 4;
    result.pastMonth.risk = RiskLevel.HIGH;
  } else if (isPast1MonthTrue(questions[2])) {
    result.pastMonth.score = 3;
    result.pastMonth.risk = RiskLevel.HIGH;
  } else if (isPast1MonthTrue(questions[1])) {
    result.pastMonth.score = 2;
    result.pastMonth.risk = RiskLevel.MEDIUM;
  } else if (isPast1MonthTrue(questions[0])) {
    result.pastMonth.score = 1;
    result.pastMonth.risk = RiskLevel.LOW;
  }

  return result;
}
export function getCssrsLifetimeSurveyBadgeColumns(
  id: string,
  jsonResults: Record<string, any>,
) {
  const results = calculateCssrsLifetimeScoreAndRiskLabel(jsonResults);
  return (
    <>
      <ChakraGridItem colSpan={2}>
        <ChakraFlex alignItems="center" data-testid="cssrs-lifetime-score">
          <ChakraText {...paragraphBold} marginRight="14px">
            {results.lifetime.score}
          </ChakraText>
          <SurveyRiskBadge
            id={id}
            label={CssrsRiskLevelToLabelMap[results.lifetime.risk]}
            riskLevel={results.lifetime.risk}
          />
        </ChakraFlex>
      </ChakraGridItem>
      <ChakraGridItem colSpan={2}>
        <ChakraFlex alignItems="center" data-testid="cssrs-past-month-score">
          <ChakraText {...paragraphBold} marginRight="14px">
            {results.pastMonth.score}
          </ChakraText>
          <SurveyRiskBadge
            id={id}
            label={CssrsRiskLevelToLabelMap[results.pastMonth.risk]}
            riskLevel={results.pastMonth.risk}
          />
        </ChakraFlex>
      </ChakraGridItem>
    </>
  );
}

function isSinceLastContactQuestionTrue(
  slcQuestion: [{ ['Since Last Contact']?: boolean }],
) {
  return slcQuestion && slcQuestion[0]?.['Since Last Contact'] === true;
}

export function calculateCssrsSinceLastContactScoreAndRisk(
  jsonResults: Record<string, any>,
): { score: number; risk: RiskLevel } {
  const result = { score: 0, risk: RiskLevel.LOW };
  const questions = [
    jsonResults['c-ssrs-slc-1'],
    jsonResults['c-ssrs-slc-2'],
    jsonResults['c-ssrs-slc-3'],
    jsonResults['c-ssrs-slc-4'],
    jsonResults['c-ssrs-slc-5'],
  ];

  if (isSinceLastContactQuestionTrue(questions[4])) {
    result.score = 5;
    result.risk = RiskLevel.HIGH;
  } else if (isSinceLastContactQuestionTrue(questions[3])) {
    result.score = 4;
    result.risk = RiskLevel.HIGH;
  } else if (isSinceLastContactQuestionTrue(questions[2])) {
    result.score = 3;
    result.risk = RiskLevel.HIGH;
  } else if (isSinceLastContactQuestionTrue(questions[1])) {
    result.score = 2;
    result.risk = RiskLevel.MEDIUM;
  } else if (isSinceLastContactQuestionTrue(questions[0])) {
    result.score = 1;
    result.risk = RiskLevel.LOW;
  }

  return result;
}
export function getCssrsSinceLastContactScoreAndRiskBadge(
  id: string,
  jsonResults: Record<string, any>,
) {
  const results = calculateCssrsSinceLastContactScoreAndRisk(jsonResults);
  return (
    <>
      <ChakraGridItem colSpan={2}>
        <ChakraFlex alignItems="center" data-testid="cssrs-last-contact-score">
          <ChakraText {...paragraphBold} marginRight="14px">
            {results.score}
          </ChakraText>
          <SurveyRiskBadge
            id={id}
            label={CssrsRiskLevelToLabelMap[results.risk]}
            riskLevel={results.risk}
          />
        </ChakraFlex>
      </ChakraGridItem>
    </>
  );
}

function isSlcScreenQuestionTrue(
  slcQuestion: [{ ['Since last contact']?: boolean }],
) {
  return slcQuestion && slcQuestion[0]?.['Since last contact'] === true;
}

export function calculateCssrsSlcScreenRisk(
  jsonResults: Record<string, any>,
): RiskLevel {
  let riskLevel = RiskLevel.LOW;
  const questions = [
    jsonResults['c-ssrs-slc-short-1'],
    jsonResults['c-ssrs-slc-short-2'],
    jsonResults['c-ssrs-slc-short-3'],
    jsonResults['c-ssrs-slc-short-4'],
    jsonResults['c-ssrs-slc-short-5'],
    jsonResults['c-ssrs-slc-short-6'],
  ];
  if (
    isSlcScreenQuestionTrue(questions[5]) ||
    isSlcScreenQuestionTrue(questions[4]) ||
    isSlcScreenQuestionTrue(questions[3]) ||
    isSlcScreenQuestionTrue(questions[2])
  ) {
    riskLevel = RiskLevel.HIGH;
  } else if (isSlcScreenQuestionTrue(questions[1])) {
    riskLevel = RiskLevel.MEDIUM;
  } else if (isSlcScreenQuestionTrue(questions[0])) {
    riskLevel = RiskLevel.LOW;
  }
  return riskLevel;
}

export function getCssrsSlcScreenRiskBadge(
  id: string,
  jsonResults: Record<string, any>,
) {
  const riskLevel = calculateCssrsSlcScreenRisk(jsonResults);

  return (
    <>
      <ChakraGridItem colSpan={2} data-testid="cssrs-slc-screen-risk-score">
        <SurveyRiskBadge
          id={id}
          label={CssrsRiskLevelToLabelMap[riskLevel]}
          riskLevel={riskLevel}
        />
      </ChakraGridItem>
    </>
  );
}

export function getNiaaaBstadSurveyBadgeColumns(
  id: string,
  jsonResults: Record<string, any>,
): ReactElement {
  const age = getAgeFromDob(jsonResults?.niaaa_pt_dob);
  const niaaa_friends_drinking_presence =
    jsonResults?.niaaa_friends_drinking_presence;
  const niaaa_pt_drinking_presence = jsonResults?.niaaa_pt_drinking_presence;
  const niaaa_pt_other_substances_presence =
    jsonResults?.niaaa_pt_other_substances_presence;
  const bstad_tobacco_use = jsonResults?.bstad_tobacco_use;
  const bstad_number_of_drinks = jsonResults?.bstad_number_of_drinks;
  const bstad_marijuana_use = jsonResults?.bstad_marijuana_use;
  const crafft_oui_presence = jsonResults?.crafft_oui_presence;
  const crafft_use_to_relax_feel_better_fit_in =
    jsonResults?.crafft_use_to_relax_feel_better_fit_in;
  const crafft_use_alone = jsonResults?.crafft_use_alone;
  const crafft_forget_things = jsonResults?.crafft_forget_things;
  const crafft_family_friends_concern =
    jsonResults?.crafft_family_friends_concern;
  const crafft_trouble_presence = jsonResults?.crafft_trouble_presence;

  const niaaaRiskLabel = () => {
    if (age >= 9 && age <= 11) {
      if (
        niaaa_friends_drinking_presence === false &&
        niaaa_pt_drinking_presence === false &&
        niaaa_pt_other_substances_presence === false
      ) {
        return (
          <SurveyRiskBadge id={id} label="Low risk" riskLevel={RiskLevel.LOW} />
        );
      }
      if (
        niaaa_friends_drinking_presence === true &&
        niaaa_pt_drinking_presence === false &&
        niaaa_pt_other_substances_presence === false
      ) {
        return (
          <SurveyRiskBadge
            id={id}
            label="Medium risk"
            riskLevel={RiskLevel.MODERATE}
          />
        );
      }
      if (
        niaaa_pt_drinking_presence === true ||
        niaaa_pt_other_substances_presence === true
      ) {
        return (
          <SurveyRiskBadge
            id={id}
            label="High risk"
            riskLevel={RiskLevel.HIGH}
          />
        );
      }
    } else if (age >= 12 && age <= 17) {
      if (
        niaaa_pt_drinking_presence === false &&
        niaaa_pt_other_substances_presence === false
      ) {
        return (
          <SurveyRiskBadge id={id} label="Low risk" riskLevel={RiskLevel.LOW} />
        );
      }
      if (
        niaaa_pt_drinking_presence === true ||
        niaaa_pt_other_substances_presence === true
      ) {
        return (
          <SurveyRiskBadge
            id={id}
            label="High risk"
            riskLevel={RiskLevel.HIGH}
          />
        );
      }
    }
    return null;
  };

  const bstadRiskLabel = () => {
    const bstad_medication_types = jsonResults?.bstad_medication_types ?? [];
    const bstad_substance_types = jsonResults?.bstad_substance_types ?? [];

    if (
      bstad_tobacco_use === 0 &&
      bstad_number_of_drinks === 0 &&
      bstad_marijuana_use === 0 &&
      bstad_substance_types?.length === 1 &&
      bstad_medication_types?.length === 1 &&
      bstad_substance_types[0] === 'none' &&
      bstad_medication_types[0] === 'none'
    ) {
      return (
        <SurveyRiskBadge id={id} label="Low risk" riskLevel={RiskLevel.LOW} />
      );
    }
    if (
      (bstad_tobacco_use >= 1 && bstad_tobacco_use <= 5) ||
      bstad_number_of_drinks === 1 ||
      bstad_marijuana_use === 1
    ) {
      if (
        bstad_substance_types?.length === 1 &&
        bstad_medication_types?.length === 1 &&
        bstad_substance_types[0] === 'none' &&
        bstad_medication_types[0] === 'none'
      ) {
        return (
          <SurveyRiskBadge
            id={id}
            label="Medium risk"
            riskLevel={RiskLevel.MODERATE}
          />
        );
      }
    }
    if (
      bstad_tobacco_use >= 6 ||
      bstad_number_of_drinks >= 2 ||
      bstad_marijuana_use >= 2 ||
      (bstad_substance_types.length > 0 &&
        bstad_substance_types[0] !== 'none') ||
      (bstad_medication_types.length > 0 &&
        bstad_medication_types[0] !== 'none')
    ) {
      return (
        <SurveyRiskBadge id={id} label="High risk" riskLevel={RiskLevel.HIGH} />
      );
    }
    return null;
  };

  const crafftRiskLabel = () => {
    const crafftResponses = [
      crafft_oui_presence,
      crafft_use_to_relax_feel_better_fit_in,
      crafft_use_alone,
      crafft_forget_things,
      crafft_family_friends_concern,
      crafft_trouble_presence,
    ];

    const trueResponsesCount = crafftResponses.filter(
      (response) => response === true,
    )?.length;
    const falseResponsesCount = crafftResponses.filter(
      (response) => response === false,
    )?.length;

    if (
      falseResponsesCount === crafftResponses?.length ||
      trueResponsesCount === 1
    ) {
      return (
        <SurveyRiskBadge
          id={id}
          label="Medium risk"
          riskLevel={RiskLevel.MODERATE}
        />
      );
    }
    if (trueResponsesCount >= 2) {
      return (
        <SurveyRiskBadge id={id} label="High risk" riskLevel={RiskLevel.HIGH} />
      );
    }
    return null;
  };

  return (
    <>
      <ChakraGridItem colSpan={2}>{niaaaRiskLabel() ?? '--'}</ChakraGridItem>
      <ChakraGridItem colSpan={2}>{bstadRiskLabel() ?? '--'}</ChakraGridItem>
      <ChakraGridItem colSpan={2}>{crafftRiskLabel() ?? '--'}</ChakraGridItem>
    </>
  );
}

export function getPhq8SurveyBadgeRiskBadge(score: number, id: string) {
  if (inRange(score, 1, 5)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Minimal depression"
        riskLevel={RiskLevel.LOW}
      />
    );
  }

  if (inRange(score, 5, 10)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Mild depression"
        riskLevel={RiskLevel.MILD}
      />
    );
  }

  if (inRange(score, 10, 15)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Moderate depression"
        riskLevel={RiskLevel.MODERATE}
      />
    );
  }

  if (inRange(score, 15, 20)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Moderately severe depression"
        riskLevel={RiskLevel.MODERATELY_SEVERE}
      />
    );
  }

  if (inRange(score, 20, 28)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Severe depression"
        riskLevel={RiskLevel.HIGH}
      />
    );
  }

  return null;
}

export function getGad7SurveyRiskBadge(score: number, id: string) {
  if (inRange(score, 1, 5)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Minimal anxiety"
        riskLevel={RiskLevel.LOW}
      />
    );
  }

  if (inRange(score, 5, 10)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Mild anxiety"
        riskLevel={RiskLevel.MILD}
      />
    );
  }

  if (inRange(score, 10, 15)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Moderate anxiety"
        riskLevel={RiskLevel.MODERATE}
      />
    );
  }

  if (inRange(score, 15, 22)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Severe anxiety"
        riskLevel={RiskLevel.HIGH}
      />
    );
  }

  return null;
}

export function getAuditSurveyRiskBadge(score: number, id: string) {
  if (inRange(score, 0, 8)) {
    return (
      <SurveyRiskBadge id={id} label="Low risk" riskLevel={RiskLevel.LOW} />
    );
  }
  if (inRange(score, 8, 15)) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Medium risk"
        riskLevel={RiskLevel.MODERATELY_SEVERE}
      />
    );
  }
  if (inRange(score, 15, Infinity)) {
    return (
      <SurveyRiskBadge id={id} label="High risk" riskLevel={RiskLevel.HIGH} />
    );
  }
  return null;
}

export function getDuditSurveyRiskBadge(
  score: number,
  id: string,
  patientGender: string,
): ReactElement | null {
  if (patientGender) {
    const lowerCaseGender = patientGender.toLowerCase();

    const {
      CIS_GENDER_FEMALE,
      CIS_GENDER_MALE,
      NON_BINARY,
      OTHER,
      TRANSGENDER_FEMALE,
      TRANSGENDER_MALE,
      UNKNOWN,
    } = PatientGender;

    if (
      (lowerCaseGender === CIS_GENDER_MALE.toLowerCase() &&
        inRange(score, 0, 6)) ||
      (lowerCaseGender === CIS_GENDER_FEMALE.toLowerCase() &&
        inRange(score, 0, 2)) ||
      ((lowerCaseGender === TRANSGENDER_MALE.toLowerCase() ||
        lowerCaseGender === TRANSGENDER_FEMALE.toLowerCase() ||
        lowerCaseGender === NON_BINARY.toLowerCase() ||
        lowerCaseGender === OTHER.toLowerCase() ||
        lowerCaseGender === UNKNOWN.toLowerCase()) &&
        inRange(score, 0, 5))
    ) {
      return (
        <SurveyRiskBadge id={id} label="Low risk" riskLevel={RiskLevel.LOW} />
      );
    }

    if (
      (lowerCaseGender === CIS_GENDER_MALE.toLowerCase() &&
        inRange(score, 6, 25)) ||
      (lowerCaseGender === CIS_GENDER_FEMALE.toLowerCase() &&
        inRange(score, 2, 25)) ||
      ((lowerCaseGender === TRANSGENDER_MALE.toLowerCase() ||
        lowerCaseGender === TRANSGENDER_FEMALE.toLowerCase() ||
        lowerCaseGender === NON_BINARY.toLowerCase() ||
        lowerCaseGender === OTHER.toLowerCase() ||
        lowerCaseGender === UNKNOWN.toLowerCase()) &&
        inRange(score, 5, 25))
    ) {
      return (
        <SurveyRiskBadge
          id={id}
          label="Medium risk"
          riskLevel={RiskLevel.MODERATELY_SEVERE}
        />
      );
    }

    if (inRange(score, 25, Infinity)) {
      return (
        <SurveyRiskBadge id={id} label="High risk" riskLevel={RiskLevel.HIGH} />
      );
    }
  }
  return (
    <SurveyBadge
      bgColor="neutral.line"
      id={id}
      label="Risk not calculated"
      labelColor="neutral.primary"
    />
  );
}

export function calculateTotalScoreAndRiskLevelInAims(
  jsonResults: string,
): Array<number> {
  const questionKey = 'aims';
  const scoreQuestionKeys = [
    `${questionKey}_1`,
    `${questionKey}_2`,
    `${questionKey}_3`,
    `${questionKey}_4`,
    `${questionKey}_5`,
    `${questionKey}_6`,
    `${questionKey}_7`,
    `${questionKey}_8`,
    `${questionKey}_9`,
    `${questionKey}_10`,
  ];
  const totalScore = sumSpecificKeysLegacy(jsonResults, scoreQuestionKeys);
  const riskLevel = util.parseJson(jsonResults)[`${questionKey}_8`];
  return [totalScore, riskLevel];
}

interface AimsRiskBadgeProps {
  score: number;
  id: string;
  surveyInstanceStatus?: string;
}
export function AimsRiskBadge({
  score,
  id,
  surveyInstanceStatus,
}: AimsRiskBadgeProps): ReactElement {
  if (surveyInstanceStatus !== 'Completed') {
    return (
      <SurveyRiskBadge
        id={id}
        label="Risk not calculated"
        riskLevel={RiskLevel.NOT_CALCULATED}
      />
    );
  }
  if (score === 0) {
    return <SurveyRiskBadge id={id} label="None" riskLevel={RiskLevel.NONE} />;
  }
  if (score === 1) {
    return <SurveyRiskBadge id={id} label="Low" riskLevel={RiskLevel.LOW} />;
  }
  if (score === 2) {
    return <SurveyRiskBadge id={id} label="Mild" riskLevel={RiskLevel.MILD} />;
  }
  if (score === 3) {
    return (
      <SurveyRiskBadge
        id={id}
        label="Moderate"
        riskLevel={RiskLevel.MODERATE}
      />
    );
  }
  if (score === 4) {
    return <SurveyRiskBadge id={id} label="High" riskLevel={RiskLevel.HIGH} />;
  }

  return (
    <SurveyRiskBadge
      id={id}
      label="Risk not calculated"
      riskLevel={RiskLevel.NOT_CALCULATED}
    />
  );
}

export function getAimsSurveyBadgeColumns(
  id: string,
  jsonResults: string,
  surveyInstanceStatus: string,
): ReactElement {
  const [totalScore, riskLevel] =
    calculateTotalScoreAndRiskLevelInAims(jsonResults);
  return (
    <ChakraGridItem colSpan={2}>
      <ChakraFlex alignItems="center" data-testid="cssrs-lifetime-score">
        <ChakraText {...paragraphBold} marginRight="14px">
          {totalScore}
        </ChakraText>
        <AimsRiskBadge
          id={id}
          score={riskLevel}
          surveyInstanceStatus={surveyInstanceStatus}
        />
      </ChakraFlex>
    </ChakraGridItem>
  );
}
